/**
 * A mesh fingerprint — the identity a Zone's face indices are valid AGAINST
 * (ADR-031 D2). Face indices address triangles of ONE mesh: the vertex array
 * AND the index buffer that threads it. On a regenerated character the
 * positions differ; on a re-indexed one (an optimizer that reorders
 * triangles while keeping every vertex) the positions match and the same
 * face numbers address different flesh — so both buffers are hashed, and the
 * face count is carried beside the vertex count (Codex P1, #136). A zone that
 * silently relabels is worse than one that refuses.
 *
 * FNV-1a, 32-bit, two seeds, over the raw bytes of positions then indices —
 * 16 hex characters, no BigInt, one pass over ~400 KB for a 12K-vertex
 * character. An identity check, not a security hash: it tells "same mesh"
 * from "different mesh", which is all a refusal needs.
 *
 * Kernel-pure. Depends on: nothing. Depended on by: the app's zone proposer
 * (mint) and its tint door (compare).
 */

export interface MeshFingerprint {
  readonly vertexCount: number;
  readonly faceCount: number;
  /** Over positions then indices, in that order. */
  readonly positionsHash: string;
}

function fnv1a32(h: number, bytes: Uint8Array): number {
  h >>>= 0;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i]!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

const bytesOf = (a: ArrayBufferView): Uint8Array => new Uint8Array(a.buffer, a.byteOffset, a.byteLength);

/** The fingerprint of a mesh: its bind-world positions (xyz interleaved) and its triangle list. */
export function fingerprintMesh(positions: Float32Array, indices: Uint32Array | Uint16Array): MeshFingerprint {
  const p = bytesOf(positions);
  const i = bytesOf(indices);
  const a = fnv1a32(fnv1a32(0x811c9dc5, p), i).toString(16).padStart(8, '0');
  const b = fnv1a32(fnv1a32(0x9747b28c, p), i).toString(16).padStart(8, '0');
  return { vertexCount: positions.length / 3, faceCount: Math.floor(indices.length / 3), positionsHash: a + b };
}

/** Same mesh, or the reason it is not — for a refusal that names the mismatch. */
export function fingerprintMismatch(zone: MeshFingerprint, mesh: MeshFingerprint): string | null {
  if (zone.vertexCount !== mesh.vertexCount) {
    return `cut on a ${zone.vertexCount.toLocaleString()}-vertex mesh; this one has ${mesh.vertexCount.toLocaleString()}`;
  }
  if (zone.faceCount !== mesh.faceCount) {
    return `cut on a ${zone.faceCount.toLocaleString()}-face mesh; this one has ${mesh.faceCount.toLocaleString()}`;
  }
  if (zone.positionsHash !== mesh.positionsHash) {
    return `cut on a mesh with the same counts but different vertices or triangle order (${zone.positionsHash} vs ${mesh.positionsHash})`;
  }
  return null;
}
